import type { ProposalLineItem } from '@/types/proposal';
import { EXTRA_UNIT_PRICE_UNIT_SUFFIX, extraUnitPriceOf } from '@/lib/membership/extra-unit-price';

/**
 * The membership benefits summary for a membership line on a proposal — coverage,
 * recurring services, savings and waived fees, projected server-side onto
 * `line.membershipBenefits`. Shared by the classic proposal page and the
 * single-scope proposal view so a membership sold through single-scope keeps the
 * same disclosure it has on the classic document.
 */

const MEMBERSHIP_SCOPE_LABELS: Record<string, string> = {
  SERVICE: 'services',
  MATERIAL: 'materials',
  EQUIPMENT: 'equipment',
  FEE: 'fees',
};

const fmtMoney = (n: number) =>
  `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function formatEquipmentCategory(value: string): string {
  return value
    .replaceAll('_', ' ')
    .toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase())
    .replace(/\bAc\b/g, 'AC')
    .replace(/\bHvac\b/g, 'HVAC');
}

function formatMembershipFrequency(value: string | null | undefined): string {
  return value
    ? value
        .replaceAll('_', ' ')
        .toLowerCase()
        .replace(/\b\w/g, (letter) => letter.toUpperCase())
    : 'Selected billing';
}

const MEMBERSHIP_PRICE_PERIOD: Record<string, string> = {
  MONTHLY: '/month',
  BIMONTHLY: ' every 2 months',
  QUARTERLY: ' every 3 months',
  SEMIANNUAL: ' every 6 months',
  SEMI_ANNUAL: ' every 6 months',
  ANNUAL: '/year',
  UPFRONT: ' paid upfront',
};

function recurringServiceSummary(
  service: NonNullable<ProposalLineItem['membershipBenefits']>['recurringServices'][number],
): string {
  const every = Math.max(1, service.recurrenceInterval || 1);
  const cadenceUnit: Record<string, string> = { DAILY: 'day', WEEKLY: 'week', MONTHLY: 'month' };
  const unit = cadenceUnit[service.recurrenceType] ?? 'period';
  const cadence =
    service.recurrenceType === 'SEASONAL'
      ? `${service.recurrenceMonths.length} seasonal visit${
          service.recurrenceMonths.length === 1 ? '' : 's'
        } / year`
      : every === 1
        ? `Every ${unit}`
        : `Every ${every} ${unit}s`;
  const delivery =
    service.attachTo === 'PROPERTY'
      ? 'one visit for the property'
      : service.scaling === 'MULTIPLY'
        ? 'one visit per covered unit'
        : `one extended visit${
            service.extraUnitMinutes > 0
              ? ` (+${service.extraUnitMinutes} min per additional unit)`
              : ''
          }`;
  return `${cadence} · ${delivery}`;
}

export function ProposalMembershipBenefits({ lineItem }: { lineItem: ProposalLineItem }) {
  const benefits = lineItem.membershipBenefits;
  if (!benefits) return null;
  const hidePrice = lineItem.displayConfig?.hidePrice === true;
  const frequency = formatMembershipFrequency(benefits.billingOptionFrequency);
  const price = (() => {
    if (hidePrice) return '';
    const currency = benefits.currency || 'USD';
    try {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(
        benefits.priceAmount,
      );
    } catch {
      // Older plan records may contain a non-ISO currency. Keep the stated
      // amount/currency readable instead of crashing or inventing a currency.
      return `${benefits.priceAmount.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })} ${currency}`;
    }
  })();
  const scopes = benefits.eligibleLineItemTypes
    .map((type) => MEMBERSHIP_SCOPE_LABELS[type.toUpperCase()])
    .filter((value): value is string => Boolean(value));
  const savings =
    benefits.customRateCount > 0
      ? `${benefits.customRateCount} custom member rate${benefits.customRateCount === 1 ? '' : 's'}`
      : benefits.discountPercent > 0
        ? `${benefits.discountPercent}% off${scopes.length ? ` ${scopes.join(' and ')}` : ''}`
        : null;

  return (
    <div className="mt-2 space-y-2 rounded-md border border-[#14141A]/[0.1] bg-white/70 p-3 text-xs text-[#63646B]">
      <p className="font-semibold text-[#33343A]">Membership benefits</p>
      <p>
        {benefits.billingOptionLabel && benefits.billingOptionLabel !== frequency
          ? `${benefits.billingOptionLabel} · ${frequency}`
          : frequency}
        {benefits.contractLengthMonths
          ? ` · ${benefits.contractLengthMonths}-month term`
          : ' · ongoing'}
      </p>
      <p>
        {hidePrice
          ? 'Membership price included in total'
          : `${price}${MEMBERSHIP_PRICE_PERIOD[benefits.billingOptionFrequency ?? ''] ?? ' — selected billing'}`}
      </p>
      {benefits.coverage.map((coverage) => (
        <p key={coverage.equipmentCategory}>
          <span className="font-medium text-[#33343A]">
            {coverage.coveredQuantity} {formatEquipmentCategory(coverage.equipmentCategory)} unit
            {coverage.coveredQuantity === 1 ? '' : 's'} covered
          </span>
          {coverage.extraUnits > 0
            ? ` · ${coverage.includedQuantity} included + ${coverage.extraUnits} additional${
                hidePrice
                  ? ''
                  : ` at ${fmtMoney(extraUnitPriceOf(coverage).extraUnitPrice)}${EXTRA_UNIT_PRICE_UNIT_SUFFIX[extraUnitPriceOf(coverage).extraUnitPricePer]} each`
              }`
            : ` · ${coverage.includedQuantity} included, no additional-unit charge`}
        </p>
      ))}
      {benefits.recurringServices.map((service, index) => (
        <p key={`${service.label}-${index}`}>
          <span className="font-medium text-[#33343A]">{service.label}</span>
          {' · '}
          {recurringServiceSummary(service)}
        </p>
      ))}
      {savings ? <p>{savings}</p> : null}
      {benefits.waivedFees.length > 0 ? (
        <p>Waived while active: {benefits.waivedFees.join(', ')}</p>
      ) : null}
    </div>
  );
}
